/**
 * Display formatters for the Overview dashboard.
 *
 * Count formatting is shared with Analytics through numberUtils.
 * Centralised here so KPI cards, table cells, and tooltips render
 * identical strings for the same logical value. Each formatter
 * returns ``--`` for ``null``/``undefined`` so the UI never falsely
 * shows ``0`` for a missing signal.
 */

import type { KpiUnit } from '../../types/overview';
import i18n from '../../i18n';
import { formatCount } from '../../utils/numberUtils';

export { formatCount };

const DASH = '--';

const PERCENT_FORMATTER = new Intl.NumberFormat(undefined, {
  style: 'percent',
  maximumFractionDigits: 1,
});

const SCORE_FORMATTER = new Intl.NumberFormat(undefined, {
  maximumFractionDigits: 2,
  minimumFractionDigits: 2,
});

const SIGNED_PERCENT_FORMATTER = new Intl.NumberFormat(undefined, {
  style: 'percent',
  signDisplay: 'always',
  maximumFractionDigits: 1,
});

const RELATIVE_TIME_SUPPORTED =
  typeof Intl !== 'undefined' && 'RelativeTimeFormat' in Intl;

// Relative-time output must follow the app's selected language, not the JS
// runtime default locale. We cache one formatter per language so switching
// language (or a `?lng=` override) picks up a correctly-localized instance
// without rebuilding on every call.
const relativeTimeFormatterCache = new Map<string, Intl.RelativeTimeFormat>();

function getRelativeTimeFormatter(): Intl.RelativeTimeFormat | null {
  if (!RELATIVE_TIME_SUPPORTED) return null;
  const lang = i18n.language || 'en';
  let formatter = relativeTimeFormatterCache.get(lang);
  if (!formatter) {
    formatter = new Intl.RelativeTimeFormat(lang, { numeric: 'auto' });
    relativeTimeFormatterCache.set(lang, formatter);
  }
  return formatter;
}

export function formatPercent(value: number | null | undefined): string {
  if (value == null) return DASH;
  return PERCENT_FORMATTER.format(value);
}

export function formatScore(value: number | null | undefined): string {
  if (value == null) return DASH;
  return SCORE_FORMATTER.format(value);
}

export function formatKpi(value: number, unit: KpiUnit | null): string {
  switch (unit) {
    case 'percent': return formatPercent(value);
    case 'score':   return formatScore(value);
    case 'ms':      return `${formatCount(value)} ms`;
    case 'count':
    default:        return formatCount(value);
  }
}

/** Render a signed percent delta or ``--``. Returns sign hint for colouring. */
export interface DeltaDisplay {
  text: string;
  trend: 'up' | 'down' | 'flat' | 'none';
}

export function formatDelta(delta: number | null | undefined): DeltaDisplay {
  if (delta == null) return { text: DASH, trend: 'none' };
  if (delta === 0)   return { text: '0%',  trend: 'flat' };
  return {
    text: SIGNED_PERCENT_FORMATTER.format(delta),
    trend: delta > 0 ? 'up' : 'down',
  };
}

/** Qualitative bucket used to tint a KPI value or a table pill. */
export type QualityBucket = 'good' | 'warn' | 'bad' | 'muted';

/**
 * Map a 0..1 percent (e.g. health/accuracy) to a qualitative bucket.
 *
 * Thresholds picked to match the mockup's colour story: >=80% green,
 * 60..80% amber, <60% red. ``null`` falls into ``muted`` so we never
 * mis-paint a missing value.
 */
export function percentBucket(value: number | null | undefined): QualityBucket {
  if (value == null) return 'muted';
  if (value >= 0.8) return 'good';
  if (value >= 0.6) return 'warn';
  return 'bad';
}

/**
 * Map a 0..5 quality score to a qualitative bucket. >=4 green,
 * 3..4 amber, <3 red. Keeps the same Good/Fair/Poor mental model
 * as ``percentBucket`` so the dashboard reads consistently.
 */
export function scoreBucket(value: number | null | undefined): QualityBucket {
  if (value == null) return 'muted';
  if (value >= 4) return 'good';
  if (value >= 3) return 'warn';
  return 'bad';
}

/**
 * Map overview content quality to the same colour story used by the
 * document quality badge: green >= 7, amber >= 4, red < 4.
 */
export function contentQualityBucket(value: number | null | undefined): QualityBucket {
  if (value == null) return 'muted';
  if (value >= 7) return 'good';
  if (value >= 4) return 'warn';
  return 'bad';
}

/** "5 minutes ago" / "2 days ago" / "in 1 hour" / DASH. */
export function formatRelativeTime(iso: string | null | undefined, now = Date.now()): string {
  if (!iso) return DASH;
  const t = Date.parse(iso);
  if (Number.isNaN(t)) return DASH;
  const diffMs = t - now;
  const absSec = Math.abs(diffMs) / 1000;

  const units: [Intl.RelativeTimeFormatUnit, number][] = [
    ['year',   31_536_000],
    ['month',  2_592_000],
    ['week',   604_800],
    ['day',    86_400],
    ['hour',   3_600],
    ['minute', 60],
    ['second', 1],
  ];

  for (const [unit, secondsInUnit] of units) {
    if (absSec >= secondsInUnit || unit === 'second') {
      const valueInUnit = Math.round(diffMs / 1000 / secondsInUnit);
      const formatter = getRelativeTimeFormatter();
      if (formatter) {
        return formatter.format(valueInUnit, unit);
      }
      // Intl.RelativeTimeFormat is unavailable (very old browsers): degrade to
      // a localized absolute date rather than a hand-rolled, English-only
      // relative string. toLocaleDateString predates RelativeTimeFormat and is
      // effectively always present, so this stays locale-correct.
      return new Date(t).toLocaleDateString(i18n.language || undefined, {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
      });
    }
  }
  return DASH;
}
